import axios from 'axios';

const baseURL = process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === 'production'
    ? '/api/v1'
    : 'http://localhost:5000/api/v1');

const api = axios.create({ baseURL, withCredentials: true });

// Auth routes that must NOT trigger a token refresh (early or after a 401)
const NO_REFRESH_PATHS = ['/auth/refresh', '/auth/login', '/auth/register', '/auth/google', '/auth/totp/login', '/auth/biometric/login'];

// The access token lasts 15 minutes (server/lib/authSession.js). Refreshing it just before it
// expires, instead of after a request fails, stops an expiry from turning every request in flight
// into a 401. The retry recovered each one, but the browser logs every 401 as a console error, and
// the fetch()-based callers had no retry at all.
const REFRESH_MARGIN_MS = 60_000;

function tokenExpiresAt(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return typeof payload.exp === 'number' ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

// The stored access token, refreshed first if it expires within REFRESH_MARGIN_MS. Use this for
// any request made outside the `api` instance (fetch-based streams).
export async function freshToken() {
  const token = localStorage.getItem('token');
  if (!token) return null;
  const expiresAt = tokenExpiresAt(token);
  if (expiresAt === null || expiresAt - Date.now() > REFRESH_MARGIN_MS) return token;
  try {
    return await refreshAccess();
  } catch {
    return token; // sent anyway: a 401 then takes the usual refresh-or-sign-out path
  }
}

// Attach JWT token to every request if present
api.interceptors.request.use(async (config) => {
  const isAuthPath = NO_REFRESH_PATHS.some(p => (config.url || '').includes(p));
  const token = isAuthPath ? localStorage.getItem('token') : await freshToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Cold-start signalling: the backend (Render free tier) sleeps when idle and Neon's
// compute can cold-start, so the first request after a quiet period may 502/503/504 (or
// surface as a network error when the 503 lacks CORS headers). While we retry, broadcast
// a "server waking" status on `window` so the UI can show a reassuring message instead of
// a silent spinner. Cleared on the first success or the final failure.
const SERVER_STATUS_EVENT = 'agence:server-status';
const AGENCE_LOGOUT_EVENT = 'agence:logout';
let serverWaking = false;
function setServerWaking(waking) {
  if (serverWaking === waking) return; // dedupe — avoid event spam on every success
  serverWaking = waking;
  if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
    window.dispatchEvent(new CustomEvent(SERVER_STATUS_EVENT, { detail: { waking } }));
  }
}

// Token refresh state — single concurrent refresh, pending requests queued
let isRefreshing = false;
let pendingQueue = [];
function flushQueue(newToken, error) {
  pendingQueue.forEach(({ resolve, reject }) => (error ? reject(error) : resolve(newToken)));
  pendingQueue = [];
}

// Retry safe (GET/HEAD/OPTIONS) requests on gateway/network errors up to 3 times with a
// linear backoff (4s, 8s, 12s ≈ 24s total) — Render typically warms up well within that.
api.interceptors.response.use(
  (response) => {
    setServerWaking(false);
    return response;
  },
  async (err) => {
    if (axios.isCancel(err)) return Promise.reject(err);
    const config = err.config;
    if (!config) return Promise.reject(err);

    const isNetworkError = !err.response;
    const isGatewayError = err.response && [502, 503, 504].includes(err.response.status);
    const isSafeMethod = ['get', 'head', 'options'].includes((config.method || '').toLowerCase());
    config._retryCount = config._retryCount || 0;

    if ((isNetworkError || isGatewayError) && isSafeMethod && config._retryCount < 3) {
      setServerWaking(true);
      config._retryCount += 1;
      await new Promise((resolve) => setTimeout(resolve, config._retryCount * 4000));
      return api(config);
    }

    setServerWaking(false);

    // 401 → try to refresh the access token, then replay the original request once
    const is401 = err.response?.status === 401;
    const isAuthPath = NO_REFRESH_PATHS.some(p => (config.url || '').includes(p));
    if (is401 && !config._retry && !isAuthPath) {
      if (isRefreshing) {
        // Queue request until the in-flight refresh resolves
        return new Promise((resolve, reject) => {
          pendingQueue.push({ resolve, reject });
        }).then(newToken => {
          config.headers = { ...config.headers, Authorization: `Bearer ${newToken}` };
          return api(config);
        });
      }

      config._retry = true;
      isRefreshing = true;
      try {
        const { data } = await axios.post(`${baseURL}/auth/refresh`, {}, { withCredentials: true });
        const newToken = data.token;
        localStorage.setItem('token', newToken);
        api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
        config.headers = { ...config.headers, Authorization: `Bearer ${newToken}` };
        flushQueue(newToken, null);
        return api(config);
      } catch (refreshErr) {
        flushQueue(null, refreshErr);
        localStorage.removeItem('token');
        localStorage.removeItem('hadSession');
        localStorage.removeItem('chat_session_start');
        if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
          window.dispatchEvent(new CustomEvent(AGENCE_LOGOUT_EVENT));
        }
        return Promise.reject(refreshErr);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(err);
  }
);

// Shared refresh helper — deduplicates concurrent refresh calls.
// If a refresh is already in-flight, callers queue and receive the same token.
// Use this instead of calling POST /auth/refresh directly from non-interceptor code
// (e.g. AuthContext startup, insightsCache fetch fallback) to avoid race conditions
// where two concurrent callers both consume the one-time refresh token.
export function refreshAccess() {
  if (isRefreshing) {
    return new Promise((resolve, reject) => pendingQueue.push({ resolve, reject }));
  }
  isRefreshing = true;
  return axios.post(`${baseURL}/auth/refresh`, {}, { withCredentials: true })
    .then(({ data }) => {
      const newToken = data.token;
      localStorage.setItem('token', newToken);
      api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      flushQueue(newToken, null);
      return newToken;
    })
    .catch(err => {
      flushQueue(null, err);
      localStorage.removeItem('hadSession');
      throw err;
    })
    .finally(() => { isRefreshing = false; });
}

export { SERVER_STATUS_EVENT, AGENCE_LOGOUT_EVENT };
export default api;
